import React, { useState, useEffect } from 'react'
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPin, Clock, Users, Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { generateUniqueSlug } from '../utils/slugify'
import Loading from '../components/Loading'

const Tours = () => {
  const { t, i18n } = useTranslation()
  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [priceRange, setPriceRange] = useState('all')
  const [sortBy, setSortBy] = useState('featured')
  const toursPerPage = 12

  const currentLang = i18n.language || 'vi'

  // Helper function to translate category names
  const translateCategory = (category) => {
    const categoryMap = {
      'Biển': t('destinations.beach'),
      'Núi': t('destinations.mountain'),
      'Thành phố': t('destinations.city'),
      'Nông thôn': t('destinations.countryside'),
      'Văn hóa': t('destinations.culture'),
      'Ẩm thực': t('destinations.food'),
      'Khám phá': t('destinations.adventure')
    }
    return categoryMap[category] || category
  }

  // Helper function to get translated tour data
  const getTourName = (tour) => {
    if (currentLang !== 'vi' && tour.translations && tour.translations[currentLang]) {
      return tour.translations[currentLang].name || tour.name
    }
    return tour.name
  }

  const getTourLocation = (tour) => {
    if (currentLang !== 'vi' && tour.translations && tour.translations[currentLang]) {
      return tour.translations[currentLang].location || tour.location
    }
    return tour.location
  }

  const categories = [
    { value: 'all', label: t('tours.allCategories') },
    { value: 'Biển', label: t('destinations.beach') },
    { value: 'Núi', label: t('destinations.mountain') },
    { value: 'Thành phố', label: t('destinations.city') },
    { value: 'Nông thôn', label: t('destinations.countryside') },
    { value: 'Văn hóa', label: t('destinations.culture') },
    { value: 'Ẩm thực', label: t('destinations.food') },
    { value: 'Khám phá', label: t('destinations.adventure') }
  ]

  const priceRanges = [
    { value: 'all', label: t('tours.allPrices') },
    { value: '0-5', label: t('tours.under5M') },
    { value: '5-10', label: t('tours.5to10M') },
    { value: '10-20', label: t('tours.10to20M') },
    { value: '20+', label: t('tours.over20M') }
  ]

  useEffect(() => {
    loadTours()
  }, [])

  const loadTours = async () => {
    try {
      setLoading(true)
      const toursQuery = query(
        collection(db, 'TOURS'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(toursQuery)
      const toursData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(tour => tour.disabled !== true && tour.approved === true) // Filter in code
      setTours(toursData)
    } catch (error) {
      console.error('Error loading tours:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price)
  }

  const renderStars = (rating) => {
    const stars = []
    const roundedRating = Math.round((rating || 4.5) * 2) / 2
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-4 h-4 ${i <= roundedRating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
        />
      )
    }
    return stars
  }

  // Filter tours
  const filteredTours = tours.filter(tour => {
    // Category filter
    if (selectedCategory !== 'all' && tour.category !== selectedCategory) {
      return false
    }

    // Price range filter
    if (priceRange !== 'all') {
      const price = tour.price || 0
      const [min, max] = priceRange.split('-').map(p => p === '+' ? Infinity : parseFloat(p) * 1000000)
      if (price < min || (max !== Infinity && price > max)) {
        return false
      }
    }

    return true
  })

  // Sort tours
  const sortedTours = [...filteredTours].sort((a, b) => {
    switch (sortBy) {
      case 'featured':
        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
      case 'price-asc':
        return (a.price || 0) - (b.price || 0)
      case 'price-desc':
        return (b.price || 0) - (a.price || 0)
      case 'rating':
        return (b.rating || 0) - (a.rating || 0)
      case 'name':
        return (a.name || '').localeCompare(b.name || '')
      default:
        return 0
    }
  })

  // Pagination
  const totalPages = Math.ceil(sortedTours.length / toursPerPage)
  const startIndex = (currentPage - 1) * toursPerPage
  const endIndex = startIndex + toursPerPage
  const currentTours = sortedTours.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (loading) {
    return <Loading show={true} message={t('common.loading')} />
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('tours.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('tours.found')} {tours.length} {t('tours.title').toLowerCase()}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tours.category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('tours.allCategories')}</option>
                <option value="Biển">Biển</option>
                <option value="Núi">Núi</option>
                <option value="Thành phố">Thành phố</option>
                <option value="Nông thôn">Nông thôn</option>
                <option value="Văn hóa">Văn hóa</option>
                <option value="Ẩm thực">Ẩm thực</option>
                <option value="Khám phá">Khám phá</option>
              </select>
            </div>

            {/* Price Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tours.priceRange')}
              </label>
              <select
                value={priceRange}
                onChange={(e) => {
                  setPriceRange(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('tours.allPrices')}</option>
                <option value="0-5">{t('tours.under5M')}</option>
                <option value="5-10">{t('tours.5to10M')}</option>
                <option value="10-20">{t('tours.10to20M')}</option>
                <option value="20+">{t('tours.over20M')}</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tours.sortBy')}
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="featured">{t('tours.featured')}</option>
                <option value="price-asc">{t('tours.priceAsc')}</option>
                <option value="price-desc">{t('tours.priceDesc')}</option>
                <option value="rating">{t('tours.rating')}</option>
                <option value="name">{t('tours.newest')}</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <div className="w-full px-4 py-2 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-gray-600">{t('tours.found')}</p>
                <p className="text-2xl font-bold text-blue-600">{sortedTours.length}</p>
                <p className="text-sm text-gray-600">{t('tours.title').toLowerCase()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tours Grid */}
        {currentTours.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {currentTours.map((tour) => {
                const tourSlug = generateUniqueSlug(tour.name, tour.id)
                return (
                  <Link
                    key={tour.id}
                    to={`/tours/${tourSlug}`}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={tour.imageUrl || 'https://via.placeholder.com/400x300?text=Tour+Image'}
                        alt={tour.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {tour.featured && (
                        <div className="absolute top-3 right-3 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                          ⭐ {t('tours.featured')}
                        </div>
                      )}
                      {tour.category && (
                        <div className="absolute top-3 left-3 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                          {translateCategory(tour.category)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {getTourName(tour)}
                      </h3>

                      <div className="flex items-center text-gray-600 text-sm mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="line-clamp-1">{getTourLocation(tour)}</span>
                      </div>

                      {tour.duration && (
                        <div className="flex items-center text-gray-600 text-sm mb-2">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{tour.duration}</span>
                        </div>
                      )}

                      {tour.maxGroupSize && (
                        <div className="flex items-center text-gray-600 text-sm mb-3">
                          <Users className="w-4 h-4 mr-1" />
                          <span>Tối đa {tour.maxGroupSize} người</span>
                        </div>
                      )}

                      <div className="flex items-center mb-3">
                        {renderStars(tour.rating)}
                        <span className="ml-2 text-sm text-gray-600">
                          ({tour.rating?.toFixed(1) || '4.5'})
                        </span>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500">Giá từ</p>
                          <p className="text-xl font-bold text-blue-600">
                            {formatPrice(tour.price || 0)}
                          </p>
                        </div>
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                          {t('common.viewDetails', 'Xem chi tiết')}
                        </button>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                    }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-medium ${currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                          }`}
                      >
                        {page}
                      </button>
                    )
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="px-2 text-gray-500">...</span>
                  }
                  return null
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                    }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">
              {t('tours.noToursFound', 'Không tìm thấy tour nào')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('tours.tryChangingFilters', 'Thử thay đổi bộ lọc để xem thêm tours')}
            </p>
            <button
              onClick={() => {
                setSelectedCategory('all')
                setPriceRange('all')
                setCurrentPage(1)
              }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Xóa bộ lọc
            </button>
          </div>
        )}
      </div>

      <style>{`
        .text-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .container-custom {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  )
}

export default Tours
